// Rentabilité — réservée au dirigeant (droit rentabilite.lire, administrateur seul). Aucun autre rôle ne voit ces écrans : le serveur
// refuse leurs routes (403) et l'interface ne les affiche pas.
//  - PanneauRentabiliteDevis : dans l'éditeur du devis, marge brute et bénéfice net estimés (coût RÉEL), détail par nature ;
//  - PanneauRentabiliteChantier : onglet du chantier, rentabilité réelle à date comparée au prévu, inconnus dits honnêtement ;
//  - PageRentabilite : tableau de bord (devis et chantiers récents, triables) et réglages (frais de structure ; coefficient des coûts
//    affichés si l'option correspondante est active).
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownUp, Settings2, TrendingUp } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { dateHeure, euros } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useModules } from '../../lib/modules.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, EntetePage, EtatErreur, EtatVide, Onglets, Panneau, useNotifier } from '../../components/ui.tsx';
import { Montant, pct, useEtroit, versSaisie } from '../devis/commun.tsx';
import '../../styles/rentabilite.css';

type Nature = 'materiel' | 'main_oeuvre' | 'sous_traitance' | 'frais';
interface Rentabilite {
  complet: boolean; nb_a_renseigner: number; partiel_libelle: string | null; ht: string;
  couts: Record<Nature, string | null> & { total: string | null }; heures_mo: string; heures_inconnues: number;
  marge_brute: string | null; marge_brute_pct: string | null;
  frais_structure: { mode: string; valeur: string; libelle: string; montant: string | null } | null;
  benefice_net: string | null; benefice_net_pct: string | null; messages: string[];
}
interface Reelle {
  ht: string; cout_horaire_moyen: string | null; couts: Record<Nature, string | null> & { total: string | null }; origines: Record<Nature, string>;
  marge_brute: string | null; marge_brute_pct: string | null; frais_structure: string | null; benefice_net: string | null; benefice_net_pct: string | null;
  ecart_marge_brute: string | null; inconnus: string[];
}
interface VueChantierRenta {
  natures?: Record<Nature, string>; chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean };
  devis: { id: string; number: string; rev_no: number; variant: string } | null; prevu: Rentabilite | null; reel: Reelle | null; a_date?: boolean; message?: string;
}
const NATURES: Nature[] = ['materiel', 'main_oeuvre', 'sous_traitance', 'frais'];
const LIBELLES: Record<Nature, string> = { materiel: 'Matériel acheté', main_oeuvre: 'Main-d’œuvre (coût horaire employeur)', sous_traitance: 'Sous-traitance', frais: 'Déplacements et frais' };

const Ligne = ({ libelle, valeur, fort, aide }: { libelle: ReactNode; valeur: ReactNode; fort?: boolean; aide?: ReactNode }) => (
  <div className={`devis-total ${fort ? 'devis-total-fort' : ''}`}><dt>{libelle}{aide && <span className="tres-petit discret renta-aide">{aide}</span>}</dt><dd>{valeur}</dd></div>
);
const Inconnu = ({ texte = 'Inconnu' }: { texte?: string }) => <span className="tres-petit devis-motif">{texte}</span>;
const montantOuInconnu = (v: string | null, texte?: string) => (v === null ? <Inconnu texte={texte} /> : <Montant v={v} />);
const signe = (v: string | null) => (v === null ? '' : Number(v) < 0 ? 'renta-negatif' : 'renta-positif');

/** Bloc de chiffres d'une rentabilité prévue (devis). */
export function BlocRentabilite({ r }: { r: Rentabilite }) {
  return (
    <div className="pile">
      {r.messages.map((m) => <Alerte key={m} ton={m.startsWith('Frais de structure non réglés') ? 'info' : 'ambre'}>{m}</Alerte>)}
      <dl className="devis-totaux">
        <Ligne libelle={`Prix de vente HT${r.partiel_libelle ? ` (${r.partiel_libelle})` : ''}`} valeur={<Montant v={r.ht} fort />} fort />
        {NATURES.map((n) => <Ligne key={n} libelle={`− ${LIBELLES[n]}`} valeur={montantOuInconnu(r.couts[n])}
          aide={n === 'main_oeuvre' ? `${versSaisie(r.heures_mo)} h${r.heures_inconnues ? ` (+ ${r.heures_inconnues} ligne(s) au temps à renseigner)` : ''}` : undefined} />)}
        <Ligne libelle="Marge brute" fort valeur={r.marge_brute === null ? <Inconnu /> : <span className={signe(r.marge_brute)}><Montant v={r.marge_brute} fort /> <span className="chiffre petit">{pct(r.marge_brute_pct)}</span></span>} />
        <Ligne libelle={r.frais_structure ? `− Frais de structure (${versSaisie(r.frais_structure.valeur)} ${r.frais_structure.libelle})` : '− Frais de structure'}
          valeur={r.frais_structure ? montantOuInconnu(r.frais_structure.montant) : <Inconnu texte="Non réglés" />} />
        <Ligne libelle="Bénéfice net estimé" fort valeur={r.benefice_net === null ? <Inconnu texte="Non calculé" /> : <span className={signe(r.benefice_net)}><Montant v={r.benefice_net} fort /> <span className="chiffre petit">{pct(r.benefice_net_pct)}</span></span>} />
      </dl>
    </div>
  );
}

/** Éditeur du devis : panneau « Rentabilité (dirigeant) ». Monté seulement avec le droit rentabilite.lire. */
export function PanneauRentabiliteDevis({ revisionId }: { revisionId: string }) {
  const q = useQuery({ queryKey: ['rentabilite-devis', revisionId], queryFn: () => api.get<{ prevu: Rentabilite; chantier: VueChantierRenta | null }>(`/api/devis/revisions/${revisionId}/rentabilite`) });
  return (
    <Panneau titre="Rentabilité (dirigeant)" actions={<Link className="btn btn-compact" to="/rentabilite">Tableau de bord</Link>}>
      {q.isLoading && <Chargement lignes={2} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && <>
        <p className="tres-petit discret">Coûts réels : main-d’œuvre au coût horaire employeur des taux horaires, matériel au coût d’achat. Visible par l’administrateur seulement.</p>
        <BlocRentabilite r={q.data.prevu} />
        {q.data.chantier?.chantier && <p className="petit"><Link to={`/interventions/chantiers/${q.data.chantier.chantier.id}?onglet=rentabilite`}>Voir la rentabilité réelle du chantier</Link></p>}
      </>}
    </Panneau>
  );
}

/** Chantier : rentabilité réelle à date, comparée au prévu du devis accepté. */
export function PanneauRentabiliteChantier({ chantierId }: { chantierId: string }) {
  const q = useQuery({ queryKey: ['rentabilite-chantier', chantierId], queryFn: () => api.get<VueChantierRenta>(`/api/chantiers/${chantierId}/rentabilite`) });
  const etroit = useEtroit(700);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  if (!v.prevu || !v.reel) return <Panneau titre="Rentabilité"><EtatVide titre="Aucun devis accepté" texte={v.message} /></Panneau>;
  const p = v.prevu; const r = v.reel;
  const lignes: { cle: string; libelle: string; prevu: string | null; reel: string | null; origine?: string }[] = [
    { cle: 'ht', libelle: 'Prix de vente HT', prevu: p.ht, reel: r.ht, origine: 'Devis accepté' },
    ...NATURES.map((n) => ({ cle: n, libelle: LIBELLES[n], prevu: p.couts[n], reel: r.couts[n], origine: r.origines[n] })),
    { cle: 'mb', libelle: 'Marge brute', prevu: p.marge_brute, reel: r.marge_brute },
    { cle: 'fs', libelle: 'Frais de structure', prevu: p.frais_structure?.montant ?? null, reel: r.frais_structure },
    { cle: 'bn', libelle: 'Bénéfice net', prevu: p.benefice_net, reel: r.benefice_net },
  ];
  return (
    <div className="pile-l">
      <Panneau titre={`Rentabilité ${v.a_date ? 'à date' : 'finale'} — devis ${v.devis?.number ?? ''} révision ${v.devis?.rev_no ?? ''}`}>
        <div className="pile">
          <p className="tres-petit discret">Réel = temps déclarés sur le chantier × coût horaire moyen du devis{r.cout_horaire_moyen ? ` (${euros(r.cout_horaire_moyen)} / h)` : ''}, matériel des bons de commande émis.
            Une donnée inconnue n’est jamais comptée pour zéro. Visible par l’administrateur seulement.</p>
          {r.inconnus.map((m) => <Alerte key={m} ton={m.startsWith('Frais de structure') ? 'info' : 'ambre'}>{m}</Alerte>)}
          {etroit ? (
            <ul className="renta-cartes">
              {lignes.map((l) => (
                <li key={l.cle} className="renta-carte">
                  <strong>{l.libelle}</strong>
                  <span className="rangee renta-carte-valeurs"><span>Prévu : {montantOuInconnu(l.prevu)}</span><span>Réel : {montantOuInconnu(l.reel)}</span></span>
                  {l.origine && <span className="tres-petit discret">{l.origine}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <div className="tableau-conteneur">
              <table className="tableau">
                <thead><tr><th scope="col">Élément</th><th scope="col" className="num">Prévu (devis)</th><th scope="col" className="num">Réel</th><th scope="col">Origine du réel</th></tr></thead>
                <tbody>{lignes.map((l) => (
                  <tr key={l.cle} className={['mb', 'bn'].includes(l.cle) ? 'renta-ligne-forte' : undefined}>
                    <th scope="row">{l.libelle}</th><td className="num">{montantOuInconnu(l.prevu)}</td><td className="num">{montantOuInconnu(l.reel)}</td>
                    <td className="petit secondaire">{l.origine ?? ''}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {r.ecart_marge_brute !== null && <p className="petit">Écart de marge brute (réel − prévu) : <strong className={signe(r.ecart_marge_brute)}>{euros(r.ecart_marge_brute)}</strong></p>}
        </div>
      </Panneau>
    </div>
  );
}

// ─── Page « Rentabilité » ───────────────────────────────────────────────────────────────────────
interface Tableau {
  frais_structure: { mode: string; valeur: string } | null; message_non_regles: string | null;
  devis: { revision_id: string; devis_id: string; dossier_id: string; number: string; title: string; rev_no: number; variant: string; status: string; dossier: string; demo: boolean;
    ht: string; marge_brute: string | null; marge_brute_pct: string | null; benefice_net: string | null; benefice_net_pct: string | null; complet: boolean }[];
  chantiers: { id: string; reference: string | null; title: string; status: string; demo: boolean; devis: string | null;
    prevu: { ht: string; marge_brute: string | null; benefice_net: string | null } | null;
    reel: { marge_brute: string | null; marge_brute_pct: string | null; benefice_net: string | null; inconnus: number } | null }[];
}
type Tri = 'recent' | 'ht' | 'marge' | 'benefice';

export function PageRentabilite() {
  const { peut } = useSession();
  const [onglet, setOnglet] = useState<'tableau' | 'reglages'>('tableau');
  if (!peut('rentabilite.lire')) return <EtatVide titre="Réservé au dirigeant" texte="Cet écran est réservé à l’administrateur." />;
  return (
    <div className="pile-l devis-page">
      <EntetePage titre="Rentabilité" sousTitre="Marge brute et bénéfice net estimés des devis, rentabilité réelle des chantiers. Réservé à l’administrateur." />
      <Onglets libelle="Rentabilité" actif={onglet} changer={setOnglet} onglets={[{ id: 'tableau', libelle: 'Tableau de bord' }, { id: 'reglages', libelle: 'Réglages' }]} />
      {onglet === 'tableau' ? <TableauDeBord /> : <Reglages />}
    </div>
  );
}

function TableauDeBord() {
  const q = useQuery({ queryKey: ['rentabilite-tableau'], queryFn: () => api.get<Tableau>('/api/rentabilite/tableau') });
  const [tri, setTri] = useState<Tri>('recent');
  const etroit = useEtroit(760);
  const devis = useMemo(() => {
    const l = [...(q.data?.devis ?? [])];
    const n = (v: string | null) => (v === null ? -Infinity : Number(v));
    if (tri === 'ht') l.sort((a, b) => n(b.ht) - n(a.ht));
    if (tri === 'marge') l.sort((a, b) => n(b.marge_brute_pct) - n(a.marge_brute_pct));
    if (tri === 'benefice') l.sort((a, b) => n(b.benefice_net) - n(a.benefice_net));
    return l;
  }, [q.data, tri]);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const t = q.data!;
  return (
    <div className="pile-l">
      {t.message_non_regles && <Alerte ton="info" titre="Frais de structure non réglés">{t.message_non_regles} Réglez-les dans l’onglet « Réglages ».</Alerte>}
      <Panneau serre titre={`Devis récents (${devis.length})`} actions={
        <label className="rangee renta-tri"><ArrowDownUp size={18} aria-hidden className="discret" /><span className="visuellement-cache">Trier les devis</span>
          <select className="saisie" aria-label="Trier les devis" value={tri} onChange={(e) => setTri(e.target.value as Tri)}>
            <option value="recent">Les plus récents</option><option value="ht">Prix de vente HT</option>
            <option value="marge">Marge brute (%)</option><option value="benefice">Bénéfice net (€)</option>
          </select></label>}>
        {!devis.length && <EtatVide icone={<TrendingUp />} titre="Aucun devis" />}
        {!!devis.length && (etroit ? (
          <ul className="renta-cartes">{devis.map((d) => (
            <li key={d.revision_id} className="renta-carte">
              <Link to={`/etudes/${d.dossier_id}/devis/${d.devis_id}?revision=${d.revision_id}`}><strong><span className="mono">{d.number}</span> {d.title}</strong></Link>
              <span className="tres-petit discret">{`${d.dossier} — ${d.variant} révision ${d.rev_no}`}{d.demo ? ' — Démo' : ''}</span>
              <span className="rangee renta-carte-valeurs"><span>HT <Montant v={d.ht} /></span>
                <span>Marge {d.marge_brute === null ? <Inconnu /> : <span className={signe(d.marge_brute)}>{euros(d.marge_brute)} ({pct(d.marge_brute_pct)})</span>}</span>
                <span>Net {d.benefice_net === null ? <Inconnu texte="Non calculé" /> : <span className={signe(d.benefice_net)}>{euros(d.benefice_net)}</span>}</span></span>
            </li>))}</ul>
        ) : (
          <div className="tableau-conteneur">
            <table className="tableau">
              <thead><tr><th scope="col">Devis</th><th scope="col">Dossier</th><th scope="col" className="num">Prix de vente HT</th><th scope="col" className="num">Marge brute</th>
                <th scope="col" className="num">Bénéfice net estimé</th></tr></thead>
              <tbody>{devis.map((d) => (
                <tr key={d.revision_id}>
                  <td><Link to={`/etudes/${d.dossier_id}/devis/${d.devis_id}?revision=${d.revision_id}`}><span className="mono">{d.number}</span> {d.title}</Link><span className="tres-petit discret devis-sous">{`${d.variant} — révision ${d.rev_no}`}{d.complet ? '' : ' — incomplet'}{d.demo ? ' — Démo' : ''}</span></td>
                  <td className="mono petit">{d.dossier}</td>
                  <td className="num"><Montant v={d.ht} /></td>
                  <td className="num">{d.marge_brute === null ? <Inconnu /> : <span className={signe(d.marge_brute)}>{euros(d.marge_brute)}<span className="tres-petit devis-sous">{pct(d.marge_brute_pct)}</span></span>}</td>
                  <td className="num">{d.benefice_net === null ? <Inconnu texte="Non calculé" /> : <span className={signe(d.benefice_net)}>{euros(d.benefice_net)}<span className="tres-petit devis-sous">{pct(d.benefice_net_pct)}</span></span>}</td>
                </tr>))}</tbody>
            </table>
          </div>
        ))}
      </Panneau>
      <Panneau serre titre={`Chantiers (${t.chantiers.length})`}>
        {!t.chantiers.length && <EtatVide titre="Aucun chantier issu d’un devis accepté" />}
        <ul className="renta-cartes">{t.chantiers.map((c) => (
          <li key={c.id} className="renta-carte">
            <Link to={`/interventions/chantiers/${c.id}?onglet=rentabilite`}><strong>{c.reference ? <span className="mono">{c.reference} </span> : null}{c.title}</strong></Link>
            <span className="tres-petit discret">{`Devis ${c.devis ?? '—'}`}{c.demo ? ' — Démo' : ''}</span>
            <span className="rangee renta-carte-valeurs">
              <span>Marge prévue {c.prevu?.marge_brute == null ? <Inconnu /> : euros(c.prevu.marge_brute)}</span>
              <span>Marge réelle {c.reel?.marge_brute == null ? <Inconnu /> : <span className={signe(c.reel.marge_brute)}>{euros(c.reel.marge_brute)}</span>}</span>
              <span>Net réel {c.reel?.benefice_net == null ? <Inconnu texte="Non calculé" /> : <span className={signe(c.reel.benefice_net)}>{euros(c.reel.benefice_net)}</span>}</span>
            </span>
            {!!c.reel?.inconnus && <Badge ton="ambre">{`${c.reel.inconnus} donnée(s) inconnue(s)`}</Badge>}
          </li>))}</ul>
      </Panneau>
    </div>
  );
}

function Reglages() {
  const modules = useModules();
  return (
    <div className="pile-l">
      <ReglageFraisStructure />
      {modules.marge_cachee && <ReglageCoefficients />}
    </div>
  );
}

function ReglageFraisStructure() {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({ queryKey: ['rentabilite-reglages'], queryFn: () => api.get<{ frais_structure: { mode: 'pct_ca' | 'par_heure'; valeur: string } | null;
    message_non_regles: string; historique: { mode: string | null; valeur: string | null; created_at: string; par: string | null }[] }>('/api/rentabilite/reglages') });
  const [mode, setMode] = useState<string | null>(null);
  const [valeur, setValeur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const fs = q.data!.frais_structure;
  const m = mode ?? fs?.mode ?? '';
  const v = valeur ?? (fs ? versSaisie(fs.valeur) : '');
  const enregistrer = async (vider: boolean) => {
    setEnvoi(true); setErreur(null);
    try {
      await api.put('/api/rentabilite/reglages', vider ? { mode: null, valeur: null } : { mode: m || null, valeur: v || null });
      notifier(vider ? 'Frais de structure retirés.' : 'Frais de structure enregistrés.');
      setMode(null); setValeur(null);
      await Promise.all([qc.invalidateQueries({ queryKey: ['rentabilite-reglages'] }), qc.invalidateQueries({ queryKey: ['rentabilite-tableau'] }),
        qc.invalidateQueries({ queryKey: ['rentabilite-devis'] }), qc.invalidateQueries({ queryKey: ['rentabilite-chantier'] })]);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Panneau titre="Frais de structure">
      <div className="pile">
        <p className="petit secondaire">Loyer, véhicules, assurances, salaires du bureau… Choisissez une seule façon de les répartir. Aucune valeur par défaut :
          {' '}sans réglage, le bénéfice net n’est pas calculé.</p>
        {!fs && <Alerte ton="info">{q.data!.message_non_regles}</Alerte>}
        <div className="grille-2">
          <ChampChoix libelle="Mode de calcul" vide="Non réglé" value={m} onChange={(e) => setMode(e.target.value)}
            options={[{ valeur: 'pct_ca', libelle: '% du prix de vente HT' }, { valeur: 'par_heure', libelle: '€ par heure de main-d’œuvre' }]} />
          <Champ libelle={m === 'par_heure' ? 'Montant par heure (€ HT)' : 'Pourcentage du prix de vente HT (%)'} inputMode="decimal" value={v}
            onChange={(e) => setValeur(e.target.value)} aide="Nombre positif, virgule acceptée." />
        </div>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="groupe-btn">
          <Bouton variante="principal" icone={<Settings2 aria-hidden />} chargement={envoi} disabled={!m || !v} onClick={() => enregistrer(false)}>Enregistrer</Bouton>
          {fs && <Bouton chargement={envoi} onClick={() => enregistrer(true)}>Retirer le réglage</Bouton>}
        </div>
        {!!q.data!.historique.length && (
          <details className="details-44"><summary className="petit">Historique</summary>
            <ul className="devis-liste-simple petit">{q.data!.historique.map((h, i) => (
              <li key={i}>{`${dateHeure(h.created_at)}${h.par ? ` — ${h.par}` : ''} : ${h.mode === null ? 'non réglé' : `${versSaisie(h.valeur)} ${h.mode === 'pct_ca' ? '% du prix de vente' : '€ par heure'}`}`}</li>))}</ul>
          </details>
        )}
      </div>
    </Panneau>
  );
}

function ReglageCoefficients() {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({ queryKey: ['marge-cachee'], queryFn: () => api.get<{ reglage: { coef_global: string; coef_materiel: string | null; coef_mo: string | null } | null;
    applique: { materiel: string; mo: string } | null; historique: { coef_global: string; coef_materiel: string | null; coef_mo: string | null; created_at: string; par: string | null }[] }>('/api/marge-cachee') });
  const [saisie, setSaisie] = useState<{ global: string; materiel: string; mainOeuvre: string } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const r = q.data!.reglage;
  const s = saisie ?? { global: r ? versSaisie(r.coef_global) : '1', materiel: r?.coef_materiel ? versSaisie(r.coef_materiel) : '', mainOeuvre: r?.coef_mo ? versSaisie(r.coef_mo) : '' };
  const enregistrer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      await api.put('/api/marge-cachee', { global: s.global, materiel: s.materiel || null, mainOeuvre: s.mainOeuvre || null });
      notifier('Coefficients enregistrés : ils s’appliquent aux devis en préparation.'); setSaisie(null);
      await qc.invalidateQueries({ queryKey: ['marge-cachee'] }); await qc.invalidateQueries({ queryKey: ['revision'] });
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Panneau titre="Coefficient sur les coûts affichés (administrateur)">
      <div className="pile">
        <p className="petit secondaire">Les autres rôles voient les coûts de revient multipliés par ce coefficient et calculent leur marge dessus ; le prix de vente en découle.
          {' '}Vous seul voyez le coût réel et la marge réelle. Il s’applique aux devis en préparation ; une révision émise garde le coefficient du jour de son émission.</p>
        {q.data!.applique && <p className="petit">{`Appliqué aujourd’hui : matériel × ${versSaisie(q.data!.applique.materiel)}, main-d’œuvre × ${versSaisie(q.data!.applique.mo)}${r ? '' : ' (aucun réglage : coûts affichés = coûts réels)'}.`}</p>}
        <div className="grille-3">
          <Champ libelle="Coefficient global" inputMode="decimal" value={s.global} onChange={(e) => setSaisie({ ...s, global: e.target.value })} aide="De 1 à 5 (1 = aucun effet)." />
          <Champ libelle="Matériel (facultatif)" inputMode="decimal" value={s.materiel} onChange={(e) => setSaisie({ ...s, materiel: e.target.value })} aide="Vide = coefficient global." />
          <Champ libelle="Main-d’œuvre (facultatif)" inputMode="decimal" value={s.mainOeuvre} onChange={(e) => setSaisie({ ...s, mainOeuvre: e.target.value })} aide="Vide = coefficient global." />
        </div>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="groupe-btn"><Bouton variante="principal" chargement={envoi} disabled={!s.global} onClick={enregistrer}>Enregistrer les coefficients</Bouton></div>
        {!!q.data!.historique.length && (
          <details className="details-44"><summary className="petit">Historique</summary>
            <ul className="devis-liste-simple petit">{q.data!.historique.map((h, i) => (
              <li key={i}>{`${dateHeure(h.created_at)}${h.par ? ` — ${h.par}` : ''} : global ${versSaisie(h.coef_global)}${h.coef_materiel ? `, matériel ${versSaisie(h.coef_materiel)}` : ''}${h.coef_mo ? `, main-d’œuvre ${versSaisie(h.coef_mo)}` : ''}`}</li>))}</ul>
          </details>
        )}
      </div>
    </Panneau>
  );
}
